'use client';

import React, { useState, useEffect } from 'react';
import { HiX, HiMail } from 'react-icons/hi';
import GlowingButton from '../shared/GlowingButton';
import DimensionLinkNoArrow from '../common/DimensionLinkNoArrow';
import { AnimatePresence, motion } from 'framer-motion';
import { BsSubstack } from 'react-icons/bs';
import Link from 'next/link';

interface ArticleNewsletterPopupProps {
  slug: string;
}

export default function ArticleNewsletterPopup({
  slug,
}: ArticleNewsletterPopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const lastClosed = localStorage.getItem(`newsletterPopup_${slug}`);
    const shouldShow =
      !lastClosed || Date.now() - Number(lastClosed) > 7 * 24 * 60 * 60 * 1000;

    // Newsletter popup state tracking

    if (shouldShow) {
      const handleScroll = () => {
        if (hasScrolled) return;

        const scrollPercent =
          (window.scrollY /
            (document.documentElement.scrollHeight - window.innerHeight)) *
          100;

        // Track scroll progress for popup trigger

        if (scrollPercent > 50) {
          setHasScrolled(true);
          setIsOpen(true);
          window.removeEventListener('scroll', handleScroll);
        }
      };

      // Tambahkan passive: true untuk performa yang lebih baik
      window.addEventListener('scroll', handleScroll, { passive: true });

      // Cek scroll position saat komponen dimount
      handleScroll();

      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [slug, hasScrolled]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem(`newsletterPopup_${slug}`, Date.now().toString());
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: 50, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 50, opacity: 0, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-50 w-[88vw] max-w-[260px]"
        >
          {/* Minimalist card */}
          <div className="relative flex items-start gap-3 px-4 py-3 bg-[#1A1A1A]/50 border border-white/10 backdrop-blur-sm rounded-lg">
            {/* Main content */}
            {/* Close button yang lebih elegan */}
            <button
              onClick={handleClose}
              aria-label="Close newsletter popup"
              className="absolute top-1 right-1 p-1 rounded-full text-neutral-400 hover:text-white transition-colors"
            >
              <HiX className="size-4" />
            </button>

            {/* Icon */}
            {/* <div className="p-2 rounded-md bg-[#1A1A1A]/70 border border-white/5 flex-shrink-0">
              <BsSubstack className="w-5 h-5 text-neural-400" />
            </div> */}

            <div className="p-2 rounded-md bg-[#1A1A1A]/70 border border-white/5 flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                x="0px"
                y="0px"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                className="text-neutral-400"
              >
                <path
                  fill="currentColor"
                  d="M20 22.724l-8-4.572-8 4.572V10h16V22.724zM12 15.849l6 3.428V12H6v7.276L12 15.849zM4 6H20V8H4zM4 2H20V4H4z"
                ></path>
              </svg>
              {/* className="w-5 h-5 text-neural-400" */}
            </div>

            {/* Text */}
            {/* <div className="flex flex-col text-left">
              <p className="text-sm font-semibold text-neutral-50">
                Enjoying this article?
              </p>
              <DimensionLinkNoArrow
                href="https://alkindivv.substack.com/subscribe?utm_source=article-popup&utm_medium=web&utm_campaign=article-popup"
                className="text-xs text-neutral-100 hover:text-emerald-400"
              >
                Subscribe
              </DimensionLinkNoArrow>
            </div> */}

            <div className="flex flex-col text-left">
              <p className="text-sm mb-0 font-semibold text-neutral-100">
                Enjoying this article?
              </p>
              <Link
                href="https://alkindivv.substack.com/subscribe?utm_source=article-popup&utm_medium=web&utm_campaign=article-popup"
                className="text-xs text-neutral-400 hover:text-neutral-100 underline underline-offset-2 decoration-neutral-500 hover:decoration-neutral-200"
              >
                Subscribe
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
